/**
 * Warden talents (tank), rotation rework (docs/design/classes/warden.md §5). 18 nodes; every node
 * changes a button or a proc of the new loop (Opening → Riposte → Shield Momentum → Slam → Aegis
 * Rebound → Aegis Throw / Recall, with Bastion cashed by Bulwark Strike).
 *
 * Capstones: Unbroken (Last Stand transforms Bulwark Strike into Unyielding Bulwark), Avenger (AoE
 * Resolve engine; Aegis Recall kicks and feeds Opening) and Lockdown (kicks feed Opening).
 */
import { ABILITY, AURA, CLASS } from '@keystone/content';
import type { AbilityDef, EffectSpec } from '@keystone/sim';
import { auraRef } from '../../transforms';
import type { TalentNode } from '../../types';
import { magicDamageTaken, nearTarget, nodeFor, procPassive, resource, selfAura, withIcd } from './common';

const W = ABILITY.warden;
const node = nodeFor(CLASS.warden);
export const WARDEN_CAPSTONE = 'warden.capstone';

/** Talent-only transform (Unbroken): Bulwark Strike becomes Unyielding Bulwark during Last Stand. */
export const WARDEN_TALENT_IDS = {
  unyieldingBulwark: 'warden.unyielding_bulwark',
  unyieldingBulwarkReady: 'warden.unyielding_bulwark_ready',
  /** Hidden per-cast hit counter of Aegis Recall (content/classes/warden.ts). */
  recallHits: 'warden.aegis_recall_hits',
} as const;

const gain = (auraId: string, stacks?: number): EffectSpec => ({ type: 'applyAura', to: 'self', aura: auraRef(auraId), ...(stacks ? { stacks } : {}) });

const unyieldingBulwark: AbilityDef = {
  id: WARDEN_TALENT_IDS.unyieldingBulwark,
  name: 'Unyielding Bulwark',
  school: 'physical',
  description: 'Strike the target and heal as if you had 6 Bastion (29% of the damage taken in the last 5 s, min 15.4% max HP). Costs no Resolve and keeps your Bastion.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  cooldown: { durationMs: 4000 },
  usableIf: { type: 'selfHasAura', auraId: WARDEN_TALENT_IDS.unyieldingBulwarkReady },
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 1.2, variance: 0.05 } },
    { type: 'heal', to: 'self', amount: { pctRecentDamageTaken: { pct: 0.286, windowMs: 5000, minPctMaxHp: 0.154 } } },
  ],
  ui: { tags: ['heal', 'defensive'], color: '#c84b4b', animAs: W.bulwarkStrike },
};

export const WARDEN_TREE: readonly TalentNode[] = [
  // ---------------------------------------------------------------- tier 1
  node({
    id: 'warden.t1.slam_interrupt',
    name: 'Concussive Slam',
    description: 'Shield Slam also interrupts a casting target (3 s lockout). The interrupt recharges in 20 s.',
    tier: 1,
    col: 0,
    cost: 2,
    effect: [
      {
        op: 'addEffects',
        abilityId: W.shieldSlam,
        effects: [
          withIcd('talent.warden.slam_interrupt_icd', 'Concussive Slam (recharging)', 20000, { type: 'targetIsCasting', interruptibleOnly: true }, [
            { type: 'interrupt', lockoutMs: 3000 },
          ]),
        ],
      },
    ],
  }),
  node({
    id: 'warden.t1.wide_ground',
    name: 'Hallowed Expanse',
    description: 'Consecrated Ground covers a 30% larger radius.',
    tier: 1,
    col: 1,
    cost: 2,
    effect: [{ op: 'modifyArea', abilityId: W.consecratedGround, radiusMult: 1.3 }],
  }),
  node({
    id: 'warden.t1.bash_charges',
    name: 'Bulwark Drills',
    description: 'Shield Wall Bash recharges in 15 s instead of 18 s, and grants Opening if you have none.',
    tier: 1,
    col: 2,
    cost: 1,
    effect: [
      { op: 'modifyCooldown', abilityId: W.shieldWallBash, deltaMs: -3000 },
      // Harness tuning: only when you have no Opening (SWB on cooldown otherwise floods and wastes it).
      { op: 'addEffects', abilityId: W.shieldWallBash, effects: [{ type: 'conditional', cond: { type: 'selfLacksAura', auraId: AURA.opening }, then: [gain(AURA.opening)] }] },
    ],
  }),
  node({
    id: 'warden.t1.resounding_slam',
    name: 'Resounding Slam',
    description: 'Shield Slam generates 25 Resolve instead of 15.',
    tier: 1,
    col: 3,
    cost: 1,
    effect: [{ op: 'modifyResource', abilityId: W.shieldSlam, field: 'gain', resource: 'resolve', amount: 25 }],
  }),
  node({
    id: 'warden.t1.sweeping_guard',
    name: 'Sweeping Guard',
    description: 'Cleave Strike hits up to 8 enemies in a 25% larger area.',
    tier: 1,
    col: 4,
    cost: 2,
    effect: [{ op: 'modifyArea', abilityId: W.cleaveStrike, maxTargetsDelta: 3, radiusMult: 1.25 }],
  }),
  node({
    id: 'warden.t1.challenging_roar',
    name: 'Challenging Roar',
    description: 'Taunt also taunts up to 4 other enemies within 10 yd of the target.',
    tier: 1,
    col: 5,
    cost: 2,
    effect: [{ op: 'addEffects', abilityId: W.taunt, effects: [{ type: 'taunt', to: 'area', area: nearTarget(10, 4), durationMs: 3000 }] }],
  }),
  node({
    id: 'warden.t1.ricochet',
    name: 'Ricochet Aegis',
    description: 'Aegis Throw bounces to 2 more enemies (5 in total), and every bounce also interrupts.',
    tier: 1,
    col: 6,
    cost: 2,
    effect: [
      // Widens the bounce, the struck markers and the Aegis Rebound bounce together.
      { op: 'modifyArea', abilityId: W.aegisThrow, maxTargetsDelta: 2 },
      { op: 'addEffects', abilityId: W.aegisThrow, effects: [{ type: 'interrupt', to: 'area', area: nearTarget(12, 5), lockoutMs: 3000 }] },
    ],
  }),

  // ---------------------------------------------------------------- tier 2
  node({
    id: 'warden.t2.shield_mastery',
    name: 'Shield Mastery',
    description: 'Cleave Strike has a 20% chance to grant Shield Momentum (resets Shield Slam). The chance rises after each miss.',
    tier: 2,
    col: 0,
    cost: 3,
    effect: [
      procPassive('talent.warden.shield_mastery', 'Shield Mastery', {
        on: 'castResolved',
        abilityIds: [W.cleaveStrike],
        chance: 0.2,
        badLuck: { step: 0.08 },
        effects: [gain(AURA.shieldMomentum)],
      }),
    ],
  }),
  node({
    id: 'warden.t2.bulwark_ward',
    name: 'Overflowing Bulwark',
    description: 'Bulwark Strike also shields you for 0.4% of your max HP per Bastion it consumes, for 6 s.',
    tier: 2,
    col: 1,
    cost: 2,
    effect: [
      {
        op: 'addEffects',
        abilityId: W.bulwarkStrike,
        // Before the Bastion is consumed. perStack is (1 + mult × stacks): base × 1000 per stack keeps
        // the shield linear in Bastion (the constant term is 0.0004% max HP, i.e. under 1 HP).
        position: 'start',
        effects: [
          {
            type: 'absorb',
            to: 'self',
            if: { type: 'selfHasAura', auraId: AURA.bastion },
            auraId: 'talent.warden.bulwark_ward',
            amount: { pctSourceMaxHp: 0.000004, perStack: { auraId: AURA.bastion, on: 'self', mult: 1000 } },
          },
        ],
      },
      { op: 'registerAura', aura: { id: 'talent.warden.bulwark_ward', name: 'Overflowing Bulwark', kind: 'buff', durationMs: 6000, stacking: 'refresh', absorb: {} } },
    ],
  }),
  node({
    id: 'warden.t2.sanctified_ground',
    name: 'Sanctified Ground',
    description: 'While you stand in Consecrated Ground, Shield Slam recharges twice as fast.',
    tier: 2,
    col: 2,
    cost: 2,
    prereqs: ['warden.t1.wide_ground'],
    effect: [
      {
        op: 'patchAura',
        auraId: AURA.consecratedGround,
        patch: {
          mods: [
            { stat: 'damageTakenPct', value: -0.1 },
            { stat: 'cooldownRatePct', value: 1, abilityIds: [W.shieldSlam] },
          ],
        },
      },
    ],
  }),
  node({
    id: 'warden.t2.retaliation',
    name: 'Retaliation',
    description: 'While Shield Wall Bash is active, enemies that hit you take holy damage (double threat, once per second).',
    tier: 2,
    col: 3,
    cost: 2,
    effect: [
      {
        op: 'addProc',
        auraId: AURA.shieldWallBash,
        proc: { on: 'damageTaken', icdMs: 1000, effects: [{ type: 'damage', school: 'holy', amount: { coef: 1.2 }, threatMult: 2 }] },
      },
    ],
  }),
  node({
    id: 'warden.t2.warding_bash',
    name: 'Warding Bash',
    description: 'Shield Bash also reduces the magic damage you take by 20% for 5 s.',
    tier: 2,
    col: 4,
    cost: 1,
    effect: [
      {
        op: 'addEffects',
        abilityId: W.shieldBash,
        effects: [selfAura({ id: 'talent.warden.warding_bash', name: 'Warding Bash', durationMs: 5000, mods: magicDamageTaken(-0.2) })],
      },
    ],
  }),
  node({
    id: 'warden.t2.iron_chain',
    name: 'Iron Chain',
    description: 'Grip recharges in 15 s and generates 20 Resolve and 2 Bastion.',
    tier: 2,
    col: 5,
    cost: 1,
    effect: [
      { op: 'modifyCooldown', abilityId: W.grip, durationMs: 15000 },
      { op: 'modifyResource', abilityId: W.grip, field: 'gain', resource: 'resolve', amount: 20 },
      { op: 'addEffects', abilityId: W.grip, effects: [gain(AURA.bastion, 2)] },
    ],
  }),
  // ---------------------------------------------------------------- tier 3
  node({
    id: 'warden.t3.twin_slams',
    name: 'Twin Slams',
    description: 'Shield Slam has 2 charges, and Shield Momentum grants a charge instead of resetting it.',
    tier: 3,
    col: 0,
    cost: 3,
    effect: [
      { op: 'modifyCooldown', abilityId: W.shieldSlam, charges: 2 },
      { op: 'patchAura', auraId: AURA.shieldMomentum, patch: { onApply: [{ type: 'modifyCooldown', to: 'self', abilityId: W.shieldSlam, addCharge: true }] } },
    ],
  }),
  node({
    id: 'warden.t3.counterweight',
    name: 'Counterweight',
    description: 'Blocking an attack while Shield Wall Bash is active grants 1 Bastion (once every 2 s).',
    tier: 3,
    col: 1,
    cost: 2,
    effect: [{ op: 'addProc', auraId: AURA.shieldWallBash, proc: { on: 'avoided', hitTypes: ['block'], icdMs: 2000, effects: [gain(AURA.bastion)] } }],
  }),
  node({
    id: 'warden.t3.last_stand_heal',
    name: 'Unbroken',
    description:
      'Capstone. Last Stand lasts 12 s and immediately heals you for 20% of your max HP. While it lasts, Bulwark Strike becomes Unyielding Bulwark: no Resolve cost, keeps your Bastion, heals as if at 6 Bastion (4 s cooldown).',
    tier: 3,
    col: 2,
    cost: 3,
    choiceGroup: WARDEN_CAPSTONE,
    effect: [
      { op: 'addAbility', ability: unyieldingBulwark },
      {
        op: 'addEffects',
        abilityId: W.lastStand,
        effects: [
          { type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.2 } },
          {
            type: 'applyAura',
            to: 'self',
            aura: {
              id: WARDEN_TALENT_IDS.unyieldingBulwarkReady,
              name: 'Unyielding Bulwark',
              kind: 'buff',
              durationMs: 12000,
              stacking: 'refresh',
              overrides: [{ abilityId: W.bulwarkStrike, with: WARDEN_TALENT_IDS.unyieldingBulwark }],
            },
          },
        ],
      },
      { op: 'patchAura', auraId: AURA.lastStand, patch: { durationMs: 12000 } },
    ],
  }),
  node({
    id: 'warden.t3.avenger',
    name: 'Avenger',
    description:
      'Capstone. Cleave Strike generates 4 Resolve per enemy hit, and Consecrated Ground recharges in 15 s. Aegis Recall interrupts every casting enemy in its path and grants Opening when it hits 2 or more enemies.',
    tier: 3,
    col: 3,
    cost: 3,
    choiceGroup: WARDEN_CAPSTONE,
    effect: [
      procPassive(
        'talent.warden.avenger',
        'Avenger',
        { on: 'damageDealt', abilityIds: [W.cleaveStrike], effects: [resource('resolve', 4)] },
        { on: 'castResolved', abilityIds: [W.aegisRecall], if: { type: 'selfHasAura', auraId: WARDEN_TALENT_IDS.recallHits, minStacks: 2 }, effects: [gain(AURA.opening)] },
      ),
      { op: 'modifyCooldown', abilityId: W.consecratedGround, deltaMs: -5000 },
      {
        op: 'addEffects',
        abilityId: W.aegisRecall,
        // Before the damage, so the counter-gated Opening sees the same path.
        position: 'start',
        effects: [{ type: 'interrupt', to: 'area', area: { anchor: 'caster', shape: { type: 'line', length: 30, width: 3 }, affects: 'enemies', includePrimary: true }, lockoutMs: 3000 }],
      },
    ],
  }),
  node({
    id: 'warden.t3.lockdown',
    name: 'Lockdown',
    description: 'Capstone. Shield Bash has 2 charges and generates 20 Resolve. Interrupting a cast with Shield Bash, Aegis Throw or Aegis Recall grants Opening.',
    tier: 3,
    col: 4,
    cost: 3,
    choiceGroup: WARDEN_CAPSTONE,
    effect: [
      { op: 'modifyCooldown', abilityId: W.shieldBash, charges: 2 },
      { op: 'modifyResource', abilityId: W.shieldBash, field: 'gain', resource: 'resolve', amount: 20 },
      procPassive('talent.warden.lockdown', 'Lockdown', { on: 'interruptDealt', abilityIds: [W.shieldBash, W.aegisThrow, W.aegisRecall], effects: [gain(AURA.opening)] }),
    ],
  }),
];
